import { useCallback, useEffect, useState } from "react";
import { isSameDay, parseISO } from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getPosition } from "@/lib/geo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import JobTimerCard from "./JobTimerCard";
import JobPhotosDialog from "@/components/photos/JobPhotosDialog";

export default function TodayJobs({ jobs: external, onChanged }) {
  const { user } = useAuth();
  const [own, setOwn] = useState([]);
  const [busy, setBusy] = useState(false);
  const [photoJob, setPhotoJob] = useState(null);

  const load = useCallback(() => {
    if (external) return;
    api.get("/jobs").then((r) => setOwn(r.data)).catch(() => {});
  }, [external]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); window.dispatchEvent(new Event("job-status-changed")); };
  const list = (external || own)
    .filter((j) => j.scheduled_date && j.status !== "cancelled" &&
      (isSameDay(parseISO(j.scheduled_date), new Date()) || j.status === "in_progress"))
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

  const start = async (j) => {
    setBusy(true);
    try {
      const geo = await getPosition();
      await api.put(`/jobs/${j.id}`, { status: "in_progress", ...(geo || {}) });
      toast.success("Timer started — tap Finish when the job is done");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-10" data-testid="today-jobs">
      <div className="label-caps text-muted-foreground mb-4">Today's jobs · {list.length}</div>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground rounded-2xl border border-dashed border-border p-6 text-center" data-testid="today-jobs-empty">No jobs scheduled for you today.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((j) => (
            <JobTimerCard key={j.id} job={j} busy={busy} onStart={start}
              onFinish={(job) => setPhotoJob({ job, mode: "complete" })}
              onPhotos={(job) => setPhotoJob({ job, mode: "photos" })} />
          ))}
        </div>
      )}
      <JobPhotosDialog job={photoJob?.job} mode={photoJob?.mode} user={user} onClose={() => setPhotoJob(null)} onDone={refresh} />
    </section>
  );
}
